import type {
  ApiError,
  HybridSearchPayload,
  HybridSearchSectionFields,
  LiveEvent,
  SearchHint,
} from '@setplex/tria-api'
import type { Effect, EffectParams } from 'effector'
import type { AdjustPurchaseableFn } from '~/features/payment/sse-purchases'
import type {
  EventWithPrice,
  HybridSearchPayloadWithPrice,
  TvWithPrice,
} from '~/shared/interfaces'
import type { HybridSearchSection } from './sections'

import { api, MediaTypes } from '@setplex/tria-api'
import { attach, combine, createEvent, createStore, sample } from 'effector'
import { createAction } from 'effector-action'
import { reset } from 'patronum'
import { versioning } from '~/core/flags'
import { formatLiveEventCardWithPrice } from '~/entities/live-events/events'
import { getCurrentEpgPeriod } from '~/entities/player/tv/lib'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { modules } from '~/shared/config'
import { F } from '~/shared/helpers'
import {
  extendMediaItemByLabelPrice,
  extendMediaItemByLabelPriceForHybridSearch,
} from '~/shared/tools'
import { isRequestCancelled } from '~/shared/tools/abort'
import { lt } from '~/shared/tools/semver'
import { getSearchCardKind } from './catchups'
import { isHybridSearchItemAllowed } from './modules'
import { groupHybridResultsIntoSections } from './sections'
import {
  attachCurrentWatchNowProgrammes,
  collectWatchNowEpgIds,
} from './watch-now-epg'

// last used query and last got result for that query
// used for cache MotN results to avoid spamming wbs with same queries
export const $lastQuery = createStore<string | null>(null)
const $lastResult = createStore<HybridSearchPayload[] | null>(null)
// the two endpoints answer differently for the same query, so a cached result
// only counts while the gate stays on the endpoint that produced it
const $lastSectioned = createStore<boolean | null>(null)

type HybridSearchQuery = EffectParams<typeof api.globalSearch.getHybridSearchFx>

// the caching wrapper (custom `effect` function below) loses effector's Fail
// type inference; annotate it explicitly to the real shape the Nora adapter
// throws, so `.fail`/`.finally` consumers below are typed accurately
export const getHybridSearchFx: Effect<
  HybridSearchQuery,
  HybridSearchPayload[],
  ApiError
> = attach({
  source: {
    lastQuery: $lastQuery,
    lastResult: $lastResult,
    lastSectioned: $lastSectioned,
    sectioned: versioning.$isSectionedSearchEnabled,
    moduleFlags: modules.$visibility,
  },
  async effect(
    { lastQuery, lastResult, lastSectioned, sectioned, moduleFlags },
    query: HybridSearchQuery
  ) {
    const results =
      query.q === lastQuery && lastSectioned === sectioned && lastResult != null
        ? lastResult
        : await api.globalSearch.getHybridSearchAbortableFx({
            ...query,
            sectioned,
          })

    // Filter before EPG enrichment so a disabled Live/EPG module never
    // triggers `getEpgInformation` (collectWatchNowEpgIds then sees no
    // WATCH_NOW hits). `$lastResult` still stores the raw payload.
    const allowed = results.filter((item) =>
      isHybridSearchItemAllowed(item, moduleFlags)
    )

    // WATCH_NOW is `TV_CHANNEL` only — the on-air programme is a separate
    // batched EPG call, same as iOS (FP-4276). Applied after the cache ternary
    // because `$lastResult` stores the raw search payload.
    const epgIds = collectWatchNowEpgIds(allowed)
    if (epgIds.length === 0) return allowed

    try {
      const { epg } = await api.tv.getEpgInformationAbortableFx({
        epgIds,
        period: getCurrentEpgPeriod(),
      })

      return attachCurrentWatchNowProgrammes(allowed, epg, Date.now())
    } catch {
      return allowed
    }
  },
}) as unknown as Effect<HybridSearchQuery, HybridSearchPayload[], ApiError>

export const $hybridPending = createStore(false)

sample({
  clock: getHybridSearchFx,
  fn: () => true,
  target: $hybridPending,
})

sample({
  clock: getHybridSearchFx.done,
  source: $lastQuery,
  filter: (lastQuery, { params }) => params.q === lastQuery,
  fn: () => false,
  target: $hybridPending,
})

sample({
  clock: getHybridSearchFx.fail,
  source: $lastQuery,
  filter: (lastQuery, { params, error }) =>
    params.q === lastQuery && !isRequestCancelled(error),
  fn: () => false,
  target: $hybridPending,
})

export const getSearchHintsFx = attach({
  effect: api.globalSearch.getSearchHintsFx,
})

export const resetHybridSearchState = createEvent()

export const $hybridSearchResults = createStore<HybridSearchPayloadWithPrice[]>(
  []
)

function isLiveSearchItem(
  item: HybridSearchPayloadWithPrice
): item is EventWithPrice & HybridSearchSectionFields {
  return item.contentType === MediaTypes.LIVE_EVENT
}

function applyHybridSearchPurchases(
  items: HybridSearchPayloadWithPrice[],
  adjustEvent: AdjustPurchaseableFn,
  adjustChannel: AdjustPurchaseableFn,
  adjustShow: AdjustPurchaseableFn,
  adjustVod: AdjustPurchaseableFn
): HybridSearchPayloadWithPrice[] {
  return items.map((item) => {
    if (isLiveSearchItem(item)) {
      return formatLiveEventCardWithPrice(adjustEvent(item))
    }
    if (item.contentType === MediaTypes.TV_CHANNEL) {
      return extendMediaItemByLabelPriceForHybridSearch(adjustChannel(item))
    }
    if (item.contentType === MediaTypes.TV_SHOW) {
      return extendMediaItemByLabelPriceForHybridSearch(adjustShow(item))
    }
    if (item.contentType === MediaTypes.VOD) {
      return extendMediaItemByLabelPriceForHybridSearch(adjustVod(item))
    }
    return item
  })
}

// FP-4238: results grouped into sections (BR-8) instead of one merged list.
// SSE adjusters re-apply purchaseInfo after the initial search snapshot so
// channel and program cards refresh the same way Live event listings do.
export const $hybridSearchSections = combine(
  $hybridSearchResults,
  ssePurchasesModel.$liveEvent,
  ssePurchasesModel.$channel,
  ssePurchasesModel.$tvShow,
  ssePurchasesModel.$vod,
  (items, adjustEvent, adjustChannel, adjustShow, adjustVod) =>
    groupHybridResultsIntoSections(
      applyHybridSearchPurchases(
        items,
        adjustEvent,
        adjustChannel,
        adjustShow,
        adjustVod
      )
    )
)

export const buildSearchChannelOrder = (
  sections: HybridSearchSection[]
): TvWithPrice[] => {
  const channelIds = new Set<number>()
  const channels: TvWithPrice[] = []

  for (const section of sections) {
    for (const item of section.items) {
      if (
        getSearchCardKind(item) !== MediaTypes.TV_CHANNEL ||
        channelIds.has(item.id)
      ) {
        continue
      }

      channelIds.add(item.id)
      channels.push(item as TvWithPrice)
    }
  }

  return channels
}

export const captureSearchChannelOrder = createEvent<TvWithPrice[]>()
export const $searchChannelOrder = createStore<TvWithPrice[]>([]).on(
  captureSearchChannelOrder,
  (_, channels) => channels
)

const formatHybridSearchItem = (
  item: HybridSearchPayload
): HybridSearchPayloadWithPrice => {
  if (item.contentType === MediaTypes.LIVE_EVENT) {
    return formatLiveEventCardWithPrice(item as LiveEvent)
  }

  return extendMediaItemByLabelPrice(item) as HybridSearchPayloadWithPrice
}

sample({
  clock: getHybridSearchFx.done,
  source: $lastQuery,
  filter: (lastQuery, { params }) => params.q === lastQuery,
  fn: (_, { result }) => result.map(formatHybridSearchItem),
  target: $hybridSearchResults,
})

sample({
  clock: getHybridSearchFx.fail,
  source: $lastQuery,
  filter: (lastQuery, { params, error }) =>
    params.q === lastQuery && !isRequestCancelled(error),
  fn: () => [],
  target: $hybridSearchResults,
})

export const $searchHints = createStore<SearchHint[]>([]).on(
  getSearchHintsFx.doneData,
  (_, payload) =>
    (payload || []).toSorted((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
)

export const $hybridSearchAvailable = createStore<boolean>(true)

sample({
  clock: versioning.$wbsVersion,
  filter: (version) => version !== null && lt(version, [3, 21, 0]), // version < 3.21
  fn: F,
  target: $hybridSearchAvailable,
})

// reset last query and last results on new request to wbs
createAction(api.globalSearch.getHybridSearchFx, {
  target: { $lastQuery, $lastResult, $lastSectioned },
  fn(target, { q, sectioned }) {
    target.$lastQuery(q)
    target.$lastResult(null)
    target.$lastSectioned(sectioned ?? false)
  },
})

// save last results in case of successful request result
// `$lastQuery` is intentionally not cleared on failure: every downstream
// `params.q === lastQuery` filter keys on it, and clearing it here would make
// them miss (stuck pending state) whenever an older request fails or is aborted
// after a newer one has already taken over the query slot
createAction(api.globalSearch.getHybridSearchFx.finally, {
  source: $lastQuery,
  target: { $lastResult },
  fn(target, lastQuery, outcome) {
    if (outcome.status === 'done' && outcome.params.q === lastQuery) {
      target.$lastResult(outcome.result)
    }
  },
})

reset({
  clock: resetHybridSearchState,
  target: [
    $searchHints,
    $hybridSearchResults,
    $searchChannelOrder,
    $lastQuery,
    $lastResult,
    $lastSectioned,
    $hybridPending,
  ],
})
